import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Home, ClipboardCheck, Target, Layers, Youtube, BarChart3, MessageCircle, DownloadCloud, Lock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { DownloadsPanel } from '@/components/sidebar/DownloadsPanel';
import { AreaSwitcher } from '@/components/oposiciones/AreaSwitcher';
import { hasFullAppAccess } from '@/lib/authRedirect';
import { ALL_OPOS_AREA_IDS, canSwitchAllOposiciones, isBasicOposicionesModuleAllowed, isOposicionesBasicPlan } from '@/lib/oposicionesAccess';
import { QuotaUpgradeModal } from './QuotaUpgradeModal';

const navItems = [
  { id: 'home', label: 'Inicio', icon: Home, path: '/app/oposiciones-c1', tourId: '' },
  { id: 'test', label: 'Tests', icon: ClipboardCheck, path: '/app/oposiciones-c1/test', tourId: 'nav-test' },
  { id: 'caso', label: 'Supuestos', icon: Target, path: '/app/oposiciones-c1/caso', tourId: 'nav-caso' },
  { id: 'flashcards', label: 'Flashcards', icon: Layers, path: '/app/oposiciones-c1/flashcards', tourId: 'nav-flashcards' },
  { id: 'recursos', label: 'Recursos', icon: Youtube, path: '/app/oposiciones-c1/recursos', tourId: 'nav-recursos' },
  { id: 'progreso', label: 'Progreso', icon: BarChart3, path: '/app/oposiciones-c1/progreso', tourId: '' },
  { id: 'chat', label: 'Chat', icon: MessageCircle, path: '/app/oposiciones-c1/chat', tourId: 'nav-chat' },
];

export function OposicionesC1Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAuth();

  const getActiveItem = () => {
    const path = location.pathname;
    const exactMatch = navItems.find(item => item.path === path);
    if (exactMatch) return exactMatch.id;
    for (const item of navItems) {
      if (path.startsWith(item.path) && item.path !== '/app/oposiciones-c1') {
        return item.id;
      }
    }
    return 'home';
  };

  const activeItem = getActiveItem();
  const isBasic = isOposicionesBasicPlan(profile?.plan);
  const visibleNavItems = navItems;
  const [lockedLabel, setLockedLabel] = useState<string | null>(null);

  return (
    <>
      <header className="bg-white dark:bg-background border-b px-2 sm:px-3 py-2.5 flex items-center gap-2 sm:gap-3 w-full">
      <div 
        className="flex items-center gap-2 cursor-pointer shrink-0 bg-[hsl(0,0%,8%)] rounded-md pl-2 pr-1.5 py-1 ring-1 ring-[hsl(45,80%,55%)]/30" 
        onClick={() => navigate('/app/oposiciones-c1')}
      >
        <img src="/munilex-academy-logo.png" alt="MunilexAcademy" className="h-7 w-auto object-contain" />
        <span className="font-bold text-[10px] text-[hsl(45,80%,55%)] hidden xl:block ml-0.5 border-l border-[hsl(45,80%,55%)]/40 pl-2 uppercase tracking-wider whitespace-nowrap">
          C1 Admin. Estado
        </span>
      </div>

      <nav className="flex-1 min-w-0 overflow-x-auto scrollbar-hide">
        <div className="flex w-max min-w-full items-center justify-start gap-0.5 xl:justify-center">
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeItem === item.id;
            const isLocked = isBasic && !isBasicOposicionesModuleAllowed(item.id);
            return (
              <Button
                key={item.id}
                variant="ghost"
                size="sm"
                data-tour={item.tourId || undefined}
                className={cn(
                  "gap-1.5 shrink-0 h-9 text-xs sm:text-sm px-2 sm:px-2.5",
                   isActive && !isLocked && "bg-blue-100 text-blue-700 hover:bg-blue-100",
                   isLocked && "text-muted-foreground/70"
                 )}
                 onClick={() => isLocked ? setLockedLabel(item.label) : navigate(item.path)}
               >
                 <Icon className="h-4 w-4 shrink-0" />
                 <span className="hidden xl:inline">{item.label}</span>
                 {isLocked && <Lock className="h-3 w-3" />}
              </Button>
            );
          })}
        </div>
      </nav>
      {/* Downloads button */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-xs border-blue-200 hover:bg-blue-50 shrink-0 h-9 px-2 sm:px-2.5"
            title="Mis Descargas"
          >
            <DownloadCloud className="h-3.5 w-3.5" />
            <span className="hidden xl:inline">Descargas</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent side="bottom" align="end" className="w-[340px] p-0 shadow-2xl border-blue-100">
          <div className="p-3 border-b flex items-center gap-2 bg-blue-50/50">
            <DownloadCloud className="h-4 w-4 text-blue-700" />
            <span className="text-sm font-semibold text-blue-900">Mis Descargas</span>
          </div>
          <DownloadsPanel mode="oposiciones_c1" />
        </PopoverContent>
      </Popover>

      {/* Area switcher for betatesters, super_admins and multi-opposition subscribers */}
      {(hasFullAppAccess(profile) || canSwitchAllOposiciones(profile?.plan)) && (
        <AreaSwitcher 
          currentAreaId="c1" 
          accent="blue" 
          label={profile?.role === 'super_admin' ? 'Acceso total' : canSwitchAllOposiciones(profile?.plan) ? "Tus Oposiciones" : undefined}
          allowedIds={hasFullAppAccess(profile) ? undefined : canSwitchAllOposiciones(profile?.plan) ? [...ALL_OPOS_AREA_IDS] : undefined}
        />
      )}
      </header>
      <QuotaUpgradeModal
        open={lockedLabel !== null}
        onOpenChange={(open) => !open && setLockedLabel(null)}
        title={`${lockedLabel || 'Esta función'} requiere Pro`}
        description="Esta función no está incluida en el plan Básico. Mejora a Pro para desbloquearla."
        options={['pro']}
      />
    </>
  );
}
